import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { UseMutationResult } from '@tanstack/react-query'
import {
  enablePermissions as enablePermissionsRequest,
  removePermission as removePermissionRequest,
  EnablePermissionsPayload,
} from '../api/permissions.api'
import { UserPermissionTypes } from '../types'
import { usePortalMutation } from 'src/Libs/api'

interface PermissionData {
  companyId: number
  userId: number
  permissionType: UserPermissionTypes
}

type ResultCallback = (vars: PermissionData) => void

type MutationResult = UseMutationResult<void | null, unknown, PermissionData, unknown>

interface Params {
  onSuccessEnable?: ResultCallback
  onErrorEnable?: ResultCallback
  onSuccessDisable?: ResultCallback
  onErrorDisable?: ResultCallback
}

type ReturnType = [MutationResult, MutationResult]

/**
 * Custom hook to mutate permissions for user in company
 */
const useUserPermissionsMutate = ({ onErrorDisable, onErrorEnable, onSuccessDisable, onSuccessEnable }: Params): ReturnType => {
  const authFetch = useAuthFetchWithResult()

  const enablePermissions = usePortalMutation({
    mutationFn: (vars: PermissionData) => {
      const { companyId, permissionType, userId } = vars

      const payloadData = {
        [permissionType]: true,
      } as EnablePermissionsPayload

      return enablePermissionsRequest(companyId, userId, payloadData, authFetch)
    },
    onSuccess: (_, vars) => onSuccessEnable?.(vars),
    onError: (_, vars) => onErrorEnable?.(vars),
  })

  const removePermission = usePortalMutation({
    mutationFn: (vars: PermissionData) => {
      const { companyId, permissionType, userId } = vars

      return removePermissionRequest(companyId, userId, permissionType, authFetch)
    },
    onSuccess: (_, vars) => onSuccessDisable?.(vars),
    onError: (_, vars) => onErrorDisable?.(vars),
  })

  return [enablePermissions, removePermission]
}

export default useUserPermissionsMutate
